import { useParams, Link } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { tryApiRequest, type BackendProduct, API_BASE } from '../lib/api';

const SLUG_IMAGES: Record<string, string> = {
  'bmcu-standard': 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=800&q=80',
  'bmcu-pro': 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=800&q=80',
  '3d-accessories': 'https://images.unsplash.com/photo-1526379095098-d400fd0bf935?auto=format&fit=crop&w=800&q=80',
};

function productImage(slug: string) {
  return SLUG_IMAGES[slug] ?? 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=800&q=80';
}

function formatPrice(val: string) {
  if (!val) return 'Liên hệ';
  const num = Number(val);
  if (!isNaN(num)) {
    return num.toLocaleString('vi-VN') + ' VNĐ';
  }
  return val;
}

export default function ProductDetailPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState<BackendProduct | null>(null);
  const [allProducts, setAllProducts] = useState<BackendProduct[]>([]);
  const [loading, setLoading] = useState(true);

  // Gallery state
  const [images, setImages] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const tracked = useRef(false);

  useEffect(() => {
    tracked.current = false; // Reset tracker if component stays mounted but slug changes
    async function loadProduct() {
      setLoading(true);
      const response = await tryApiRequest<{ products: BackendProduct[] }>('/products', { useCache: true });
      if (response?.products) {
        setAllProducts(response.products.filter(p => p.isActive));
        const found = response.products.find((item) => item.slug === slug);
        if (found) {
          setProduct(found);
          try {
            const parsedImgs = JSON.parse(found.images || '[]');
            if (parsedImgs.length > 0) {
              setImages(parsedImgs.map((img: string) => API_BASE.replace('/api', '') + img));
            } else {
              setImages([productImage(found.slug)]);
            }
          } catch {
            setImages([productImage(found.slug)]);
          }
          setCurrentIndex(0);
          
          // Track product view event
          if (!tracked.current) {
            tracked.current = true;
            fetch(`${API_BASE}/analytics/event`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ eventType: 'view_product', targetId: found.id, targetName: found.name }),
              keepalive: true
            }).catch(err => console.error('Tracking failed', err));
          }
        }
      }
      setLoading(false);
    }

    loadProduct();
  }, [slug]);

  if (loading) {
    return <div className="page-block"></div>;
  }

  if (!product) {
    return <div className="page-block"><h1>Sản phẩm không tồn tại</h1></div>;
  }

  // Parse specs
  let specList: {key: string, value: string}[] = [];
  try {
    specList = JSON.parse(product.specs || '[]');
  } catch {}

  const goNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };
  const goPrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const relatedProducts = allProducts.filter(p => p.id !== product.id).slice(0, 3);

  return (
    <div className="page-block product-detail">
      <style>{`
        @keyframes fadeImage {
          0% { opacity: 0.4; transform: scale(0.97); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
      <div className="detail-card">
        
        {/* Gallery Section */}
        <div className="product-gallery">
          {/* Main Image (1:1 Square) */}
          <div 
            className="main-image-container" 
            style={{ position: 'relative', width: '100%', maxWidth: '500px', minWidth: 0, aspectRatio: '1 / 1', borderRadius: '24px', overflow: 'hidden', cursor: 'zoom-in', background: '#0c1018', border: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setShowLightbox(true)}
          >
            <img key={currentIndex} src={images[currentIndex]} alt="main" style={{ width: '100%', height: '100%', objectFit: 'contain', animation: 'fadeImage 0.35s ease-out forwards' }} />
            
            {images.length > 1 && (
              <>
                <button onClick={goPrev} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', background: 'rgba(15, 20, 32, 0.85)', color: '#ffffff', border: '1px solid var(--glass-border)', borderRadius: '50%', width: '40px', height: '40px', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>❮</button>
                <button onClick={goNext} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'rgba(15, 20, 32, 0.85)', color: '#ffffff', border: '1px solid var(--glass-border)', borderRadius: '50%', width: '40px', height: '40px', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>❯</button>
              </>
            )}
          </div>
          
          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="thumbnail-list" style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '8px', minWidth: 0, width: '100%', maxWidth: '500px' }}>
              {images.map((img, idx) => (
                <div 
                  key={idx} 
                  onClick={() => setCurrentIndex(idx)}
                  style={{ width: '76px', height: '76px', flexShrink: 0, borderRadius: '14px', overflow: 'hidden', background: '#0c1018', border: currentIndex === idx ? '2px solid var(--neon-red)' : '1px solid var(--glass-border)', cursor: 'pointer', opacity: currentIndex === idx ? 1 : 0.65, transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <img src={img} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="thumb" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="detail-copy">
          <h1 style={{
            fontSize: '1.85rem',
            color: '#f8fafc',
            lineHeight: '1.35',
            margin: '0 0 16px 0',
            fontWeight: 800,
            wordBreak: 'break-word'
          }}>{product.name}</h1>
          <p className="product-price" style={{ fontSize: '1.8rem', color: 'var(--neon-red)', fontWeight: 'bold', margin: '0 0 20px 0' }}>{formatPrice(product.priceDisplay)}</p>

          {/* Mở link mua hàng Button */}
          {product.ecommerceLink && product.ecommerceLink !== 'https://' ? (
            <a 
              className="button primary" 
              href={product.ecommerceLink} 
              target="_blank" 
              rel="noreferrer" 
              style={{ display: 'block', textAlign: 'center', marginBottom: '24px', padding: '14px', fontSize: '1.1rem', fontWeight: 700 }}
              onClick={(e) => {
                // Tracking fire-and-forget
                fetch(`${API_BASE}/analytics/event`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ eventType: 'BUY_CLICK', targetId: product.id, targetName: product.name }),
                  keepalive: true
                }).catch(err => console.error('Tracking failed', err));
              }}
            >
              {product.buttonText || 'Mua Ngay'}
            </a>
          ) : null}

          <ul className="check-list" style={{ marginTop: '0' }}>
            {product.features ? (
              product.features.split('\n').filter(f => f.trim()).map((feature, idx) => (
                <li key={idx}>{feature}</li>
              ))
            ) : (
              <>
                <li>Sản phẩm phân phối chính hãng</li>
                <li>Hỗ trợ kỹ thuật 24/7</li>
                <li>Bảo hành 1 đổi 1 trong 30 ngày</li>
              </>
            )}
          </ul>
        </div>
      </div>

      {/* Description Block */}
      <div className="product-description-block" style={{ marginTop: '32px', background: 'var(--glass-bg)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', padding: '32px', borderRadius: '24px', border: '1px solid var(--glass-border)', boxShadow: '0 16px 40px rgba(0, 0, 0, 0.4)' }}>
        <h2 style={{ fontSize: '1.4rem', color: '#f8fafc', marginBottom: '16px', borderBottom: '1px solid var(--glass-border)', paddingBottom: '12px' }}>Mô tả sản phẩm</h2>
        <p style={{ lineHeight: '1.8', color: '#cbd5e1', margin: '0', whiteSpace: 'pre-wrap', fontSize: '1.02rem' }}>{product.description}</p>
      </div>

      {/* Specs block restored */}
      {specList.length > 0 && (
        <div className="spec-block">
          <h2>Thông tin chi tiết</h2>
          <div className="spec-list">
            {specList.map((spec, idx) => (
              <div className="spec-row" key={idx}>
                <span>{spec.key}</span>
                <strong>{spec.value}</strong>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <div style={{ marginTop: '60px' }}>
          <h2 style={{ fontSize: '1.5rem', color: '#f8fafc', marginBottom: '24px', borderBottom: '1px solid var(--glass-border)', paddingBottom: '12px' }}>Sản phẩm gợi ý</h2>
          <div className="product-grid">
            {relatedProducts.map((relProduct) => {
              let coverImg = productImage(relProduct.slug);
              try {
                const imgs = JSON.parse(relProduct.images || '[]');
                if (imgs.length > 0) coverImg = API_BASE.replace('/api', '') + imgs[0];
              } catch {}
              
              return (
                <article className="product-card" key={relProduct.id}>
                  <Link to={`/products/${relProduct.slug}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                    <div className="product-image" style={{ backgroundImage: `url(${coverImg})` }} />
                  </Link>
                  <div className="product-body" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Link to={`/products/${relProduct.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <h3 style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        margin: '0 0 12px 0',
                        lineHeight: '1.4'
                      }}>
                        {relProduct.name}
                      </h3>
                    </Link>
                    <div className="inline-info" style={{ marginTop: 'auto', marginBottom: '16px' }}>
                      <strong style={{ fontSize: '1.25rem', color: '#dc2626' }}>{formatPrice(relProduct.priceDisplay)}</strong>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <Link 
                        className="button primary" 
                        to={relProduct.ecommerceLink && relProduct.ecommerceLink !== 'https://' ? relProduct.ecommerceLink : `/products/${relProduct.slug}`} 
                        target={relProduct.ecommerceLink && relProduct.ecommerceLink !== 'https://' ? "_blank" : undefined}
                        onClick={() => {
                          fetch(`${API_BASE}/analytics/event`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ eventType: 'BUY_CLICK', targetId: relProduct.id, targetName: relProduct.name }),
                            keepalive: true
                          }).catch(e => console.error('Tracking failed', e));
                        }}
                      >
                        {product.buttonText || 'Mua Ngay'}
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {showLightbox && (
        <div className="lightbox-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.9)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowLightbox(false)}>
          <button style={{ position: 'absolute', top: '20px', right: '30px', background: 'transparent', border: 'none', color: 'white', fontSize: '40px', cursor: 'pointer' }} onClick={() => setShowLightbox(false)}>×</button>
          
          <img key={currentIndex} src={images[currentIndex]} style={{ maxWidth: '90%', maxHeight: '90vh', objectFit: 'contain', animation: 'fadeImage 0.35s ease-out forwards' }} alt="fullscreen" onClick={e => e.stopPropagation()} />
          
          {images.length > 1 && (
            <>
              <button onClick={goPrev} style={{ position: 'absolute', left: '30px', background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', borderRadius: '50%', width: '60px', height: '60px', fontSize: '30px', cursor: 'pointer' }}>❮</button>
              <button onClick={goNext} style={{ position: 'absolute', right: '30px', background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', borderRadius: '50%', width: '60px', height: '60px', fontSize: '30px', cursor: 'pointer' }}>❯</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
